import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, ArrowRight, Shield } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PortalLandingPage() {
  const navigate = useNavigate();
  const [accessCode, setAccessCode] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleAccess = async (e) => {
    e.preventDefault();
    if (!accessCode.trim() || !password.trim()) {
      toast.error("Bitte füllen Sie beide Felder aus.");
      return;
    }

    // Extract project ID from full URL or use as-is
    let projectId = accessCode.trim();
    const match = projectId.match(/\/portal\/([a-zA-Z0-9-]+)/);
    if (match) projectId = match[1];

    setLoading(true);
    try {
      await axios.post(`${API}/portal/${projectId}/auth`, { password });
      navigate(`/portal/${projectId}`);
    } catch (err) {
      const msg = err.response?.status === 401
        ? "Falsches Passwort"
        : err.response?.status === 404
        ? "Projekt nicht gefunden"
        : "Zugang fehlgeschlagen";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-lab-bg flex flex-col">
      {/* Header */}
      <header className="py-6 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <a href="/" className="font-playfair text-2xl font-semibold tracking-tight text-lab-primary" data-testid="portal-landing-logo">
            LAB336
          </a>
        </div>
      </header>

      {/* Main */}
      <main className="flex-1 flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 border border-lab-border bg-white mb-5">
              <Shield className="w-6 h-6 text-lab-accent" />
            </div>
            <h1 className="font-playfair text-2xl sm:text-3xl font-semibold text-lab-primary mb-2" data-testid="portal-landing-title">
              Kundenportal
            </h1>
            <p className="text-sm text-lab-text-muted">
              Geben Sie Ihren Zugangscode und Ihr Passwort ein, um auf Ihr Projekt-Briefing zuzugreifen.
            </p>
          </div>

          <form onSubmit={handleAccess} className="bg-white border border-lab-border p-6 space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-lab-text-muted">Projekt-ID oder Portal-Link</label>
              <Input
                value={accessCode}
                onChange={e => setAccessCode(e.target.value)}
                className="rounded-none"
                placeholder="z.B. abc123-... oder https://lab336.ch/portal/..."
                data-testid="portal-landing-code"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-lab-text-muted">Passwort</label>
              <Input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="rounded-none"
                placeholder="Ihr Zugangspasswort"
                data-testid="portal-landing-password"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary rounded-none w-full py-2.5 text-sm flex items-center justify-center gap-2"
              data-testid="portal-landing-submit"
            >
              {loading ? (
                <span className="animate-pulse">Prüfe Zugang...</span>
              ) : (
                <>Zum Briefing <ArrowRight className="w-4 h-4" /></>
              )}
            </button>
          </form>

          <p className="text-xs text-lab-text-muted text-center mt-5">
            Sie haben Ihren Zugangscode per E-Mail oder von Ihrem Projektbetreuer erhalten.
          </p>
        </motion.div>
      </main>
    </div>
  );
}
